import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Autocomplete,
  List,
  ListItem,
  ListItemText,
  Divider,
  InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import projectService from '../../services/projectService'; 
import { Package } from '../../services/projectService';
import contractorService, { Contractor } from '../../services/contractorService';

interface AddContractDraftFormProps {
  open: boolean;
  onClose: () => void;
  projectId: string; // ID projektu (string z bazy, np. "226-227")
  onDraftAdded: () => void; // Callback po dodaniu draftu
}

const AddContractDraftForm: React.FC<AddContractDraftFormProps> = ({ open, onClose, projectId, onDraftAdded }) => {
  // Stany dla wyszukiwania kontrahentów
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Contractor[]>([]);
  const [loadingContractors, setLoadingContractors] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [subcontractorType, setSubcontractorType] = useState<'company' | 'natural_person' | ''>('');
  const [subcontractorName, setSubcontractorName] = useState('');
  const [subcontractorAddress, setSubcontractorAddress] = useState('');
  const [subcontractorCity, setSubcontractorCity] = useState('');
  const [subcontractorPostalCode, setSubcontractorPostalCode] = useState('');
  const [subcontractorRegistrationCourt, setSubcontractorRegistrationCourt] = useState('');
  const [subcontractorNip, setSubcontractorNip] = useState('');
  const [subcontractorRegon, setSubcontractorRegon] = useState('');
  const [subcontractorKrs, setSubcontractorKrs] = useState('');
  const [representativeName, setRepresentativeName] = useState('');
  const [representativePosition, setRepresentativePosition] = useState('');
  const [orderName, setOrderName] = useState(''); // Przedmiot umowy
  const [subcontractorOfferDate, setSubcontractorOfferDate] = useState<string | null>(null);
  const [netRenumeration, setNetRenumeration] = useState<string>('');
  const [contractCompletionDate, setContractCompletionDate] = useState<string | null>(null);
  const [subcontractorEmail, setSubcontractorEmail] = useState('');
  const [packageId, setPackageId] = useState<string | null>(null); // ID wybranego pakietu

  const [packages, setPackages] = useState<Package[]>([]); // Użycie poprawnego typu Package
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && projectId) {
      // Pobierz pakiety dla projektu, aby użytkownik mógł wybrać, którego dotyczy umowa
      projectService.getPackagesByProjectId(projectId)
        .then(setPackages)
        .catch(err => {
          console.error("Błąd podczas pobierania pakietów:", err);
          setError("Nie udało się załadować listy pakietów.");
        });
    }
  }, [open, projectId]);

  // Funkcja do wyszukiwania kontrahentów
  const searchContractors = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    try {
      setLoadingContractors(true);
      const filters = { searchText: query };
      const response = await contractorService.filterContractors(filters, 1, 10);
      setSearchResults(response.contractors);
      setShowResults(true);
    } catch (error) {
      console.error('Błąd podczas wyszukiwania kontrahentów:', error);
      setSearchResults([]);
    } finally {
      setLoadingContractors(false);
    }
  };

  // Obsługa wyboru kontrahenta z wyników wyszukiwania
  const handleSelectContractor = (contractor: Contractor) => {
    // Określenie typu podwykonawcy na podstawie danych
    let typeValue: 'company' | 'natural_person' | '' = '';
    if (contractor.type === 'company' || contractor.type === 'natural_person') {
      typeValue = contractor.type;
    } else {
      // Jeśli typ jest nieznany lub niepasujący, można ustawić domyślny lub pusty
      // W tym przypadku, jeśli backend zwraca np. "dostawca", ustawimy na ''
      // aby użytkownik wybrał poprawny typ z listy.
      // Alternatywnie, można próbować mapować "dostawca" na "company" lub "natural_person"
      // jeśli istnieje taka logika biznesowa. Na razie ustawiamy na pusty.
      console.warn(`Nieznany typ kontrahenta "${contractor.type}". Ustawianie na pusty.`);
    }
    
    setSubcontractorType(typeValue);
    setSubcontractorName(contractor.name);
    setSubcontractorNip(contractor.nip || '');
    setSubcontractorEmail(contractor.email || '');
    setSubcontractorAddress(contractor.address || '');
    setSubcontractorCity(contractor.location || '');
    
    // Opcjonalne pola, które mogą nie być dostępne w typie Contractor
    // Dlatego używamy dowolnego typu (any) dla bezpieczeństwa
    const contractorAny = contractor as any;
    if (typeValue === 'company' && contractorAny) { // Używamy typeValue zamiast type
      setSubcontractorRegon(contractorAny.regon || '');
      setSubcontractorKrs(contractorAny.krs || '');
    }
    
    setShowResults(false);
    setSearchQuery('');
  };

  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    if (!subcontractorType) {
      setError('Rodzaj działalności jest wymagany.');
      setLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append('project_id', projectId);
    if (packageId) {
      formData.append('package_id', packageId);
    }
    formData.append('subcontractor_type', subcontractorType);
    formData.append('subcontractor_name', subcontractorName);
    formData.append('subcontractor_address', subcontractorAddress);
    formData.append('subcontractor_city', subcontractorCity);
    formData.append('subcontractor_postal_code', subcontractorPostalCode);
    formData.append('subcontractor_registration_court', subcontractorRegistrationCourt);
    formData.append('subcontractor_nip', subcontractorNip);
    formData.append('subcontractor_regon', subcontractorRegon);
    formData.append('subcontractor_krs', subcontractorKrs);
    formData.append('representative_name', representativeName);
    formData.append('representative_position', representativePosition);
    formData.append('order_name', orderName);
    if (subcontractorOfferDate) {
      formData.append('subcontractor_offer_date', subcontractorOfferDate);
    }
    formData.append('net_renumeration', netRenumeration); // Backend powinien sparsować to do liczby
    if (contractCompletionDate) {
      formData.append('contract_completion_date', contractCompletionDate);
    }
    formData.append('subcontractor_email', subcontractorEmail);
    // Pliki będą dodawane w przyszłości, jeśli zajdzie taka potrzeba

    try {
      const response = await projectService.createContractDraft(formData); // Przekazujemy formData
      console.log("Odpowiedź z serwera po utworzeniu draftu umowy:", response);
      
      // Sprawdzamy, czy odpowiedź zawiera pole success i message
      if (response && response.success) {
        setSuccessMessage(response.message || 'Draft umowy został pomyślnie utworzony.');
        // Czekamy 1.5 sekundy, aby użytkownik mógł zobaczyć komunikat o sukcesie
        setTimeout(() => {
          onDraftAdded();
          onClose();
        }, 1500);
      } else {
        onDraftAdded(); 
        onClose(); 
      }
    } catch (err: any) {
      console.error("Błąd podczas tworzenia draftu umowy:", err);
      setError(err.response?.data?.details || err.message || 'Nie udało się utworzyć draftu umowy.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Dodaj nowy draft umowy
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required margin="normal">
                <InputLabel id="subcontractor-type-label">Rodzaj działalności</InputLabel>
                <Select
                  labelId="subcontractor-type-label"
                  value={subcontractorType}
                  label="Rodzaj działalności"
                  onChange={(e) => setSubcontractorType(e.target.value as 'company' | 'natural_person' | '')}
                >
                  <MenuItem value="company">Spółka</MenuItem>
                  <MenuItem value="natural_person">Osoba fizyczna</MenuItem>
                  {/* Dodajemy pustą opcję, jeśli jest potrzebna, lub usuwamy, jeśli nie */}
                  {/* <MenuItem value=""><em>Wybierz rodzaj</em></MenuItem> */}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ position: 'relative' }}>
                <TextField
                  fullWidth
                  label="Wyszukaj/Wybierz Podwykonawcę"
                  variant="outlined"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    searchContractors(e.target.value);
                  }}
                  margin="normal"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {loadingContractors ? (
                          <CircularProgress size={20} />
                        ) : (
                          <SearchIcon />
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
                
                {/* Wyniki wyszukiwania */}
                {showResults && searchResults.length > 0 && (
                  <Paper 
                    elevation={3} 
                    sx={{ 
                      position: 'absolute', 
                      width: '100%', 
                      maxHeight: '200px', 
                      overflow: 'auto',
                      zIndex: 1000,
                      mt: 0.5
                    }}
                  >
                    <List dense>
                      {searchResults.map((contractor) => (
                        <React.Fragment key={contractor.id}>
                          <ListItem 
                            button 
                            onClick={() => handleSelectContractor(contractor)}
                          >
                            <ListItemText 
                              primary={contractor.name} 
                              secondary={
                                <>
                                  <Typography variant="body2" component="span">
                                    {contractor.email}
                                  </Typography>
                                  <br />
                                  <Typography variant="body2" component="span" color="text.secondary">
                                    {contractor.location}, NIP: {contractor.nip}
                                  </Typography>
                                </>
                              }
                            />
                          </ListItem>
                          <Divider />
                        </React.Fragment>
                      ))}
                    </List>
                  </Paper>
                )}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                id="subcontractorName"
                label="Nazwa Podwykonawcy / Imię i Nazwisko"
                name="subcontractorName"
                value={subcontractorName}
                onChange={(e) => setSubcontractorName(e.target.value)}
                margin="normal"
              />
            </Grid>

            {/* Pola zależne od typu podwykonawcy */}
            {subcontractorType === 'company' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Sąd rejestrowy" value={subcontractorRegistrationCourt} onChange={(e) => setSubcontractorRegistrationCourt(e.target.value)} margin="normal"/>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="KRS" value={subcontractorKrs} onChange={(e) => setSubcontractorKrs(e.target.value)} margin="normal"/>
                </Grid>
              </>
            )}
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="NIP" value={subcontractorNip} onChange={(e) => setSubcontractorNip(e.target.value)} margin="normal"/>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="REGON" value={subcontractorRegon} onChange={(e) => setSubcontractorRegon(e.target.value)} margin="normal"/>
            </Grid>
             <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Adres e-mail Podwykonawcy" value={subcontractorEmail} onChange={(e) => setSubcontractorEmail(e.target.value)} margin="normal"/>
            </Grid>


            <Grid item xs={12} sm={8}>
              <TextField fullWidth label="Adres" value={subcontractorAddress} onChange={(e) => setSubcontractorAddress(e.target.value)} margin="normal"/>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Miasto" value={subcontractorCity} onChange={(e) => setSubcontractorCity(e.target.value)} margin="normal"/>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Kod pocztowy" value={subcontractorPostalCode} onChange={(e) => setSubcontractorPostalCode(e.target.value)} margin="normal"/>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Imię i nazwisko reprezentanta" value={representativeName} onChange={(e) => setRepresentativeName(e.target.value)} margin="normal"/>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Stanowisko reprezentanta" value={representativePosition} onChange={(e) => setRepresentativePosition(e.target.value)} margin="normal"/>
            </Grid>
            
            <Grid item xs={12}>
                 <FormControl fullWidth margin="normal">
                    <InputLabel id="package-id-label">Pakiet (dla linku do dokumentacji)</InputLabel>
                    <Select
                        labelId="package-id-label"
                        value={packageId || ''}
                        label="Pakiet (dla linku do dokumentacji)"
                        onChange={(e) => setPackageId(e.target.value as string)}
                    >
                        <MenuItem value="">
                            <em>Brak / Nie dotyczy</em>
                        </MenuItem>
                        {packages.map((pkg) => (
                            <MenuItem key={pkg.package_id} value={pkg.package_id}>
                                {pkg.name} ({pkg.package_symbol})
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="Przedmiot umowy (Opis z umowy)"
                value={orderName}
                onChange={(e) => setOrderName(e.target.value)}
                margin="normal"
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Data oferty Podwykonawcy"
                value={subcontractorOfferDate || ''}
                onChange={(e) => setSubcontractorOfferDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                type="number"
                label="Wynagrodzenie netto"
                value={netRenumeration}
                onChange={(e) => setNetRenumeration(e.target.value)}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Termin realizacji umowy"
                value={contractCompletionDate || ''}
                onChange={(e) => setContractCompletionDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                margin="normal"
              />
            </Grid>
          </Grid>
          {error && (
            <Typography color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
          {successMessage && (
            <Typography color="success.main" sx={{ mt: 2 }}>
              {successMessage}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="secondary">
          Anuluj
        </Button>
        <Button
          type="submit"
          variant="contained"
          onClick={handleSubmit} // Podpinamy handleSubmit również tutaj
          disabled={loading}
          sx={{ position: 'relative' }}
        >
          Dodaj Draft Umowy
          {loading && (
            <CircularProgress
              size={24}
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                marginTop: '-12px',
                marginLeft: '-12px',
              }}
            />
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddContractDraftForm;
